import React, { useState, useEffect, useMemo } from 'react';
import { Button, Modal, Form, Spinner, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarCheck,
  faMagnifyingGlass,
  faPenToSquare,
  faTrashCan,
  faPhone,
  faEnvelope,
  faCircleCheck,
  faCircleXmark,
  faEye,
  faFilter,
  faRotateRight,
  faArrowDownShortWide,
  faArrowUpShortWide
} from '@fortawesome/free-solid-svg-icons';
import {
  getAppointments,
  updateAppointmentStatus,
  deleteAppointment
} from '../../services/firebase/appointmentService';

const STATUS_META = {
  pending: { label: 'Pending', cls: 'pending' },
  confirmed: { label: 'Confirmed', cls: 'confirmed' },
  completed: { label: 'Completed', cls: 'completed' },
  cancelled: { label: 'Cancelled', cls: 'cancelled' }
};

const renderStatus = (status) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return <span className={`admin-status-pill admin-status-pill--${meta.cls}`}>{meta.label}</span>;
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const PAGE_SIZE = 10;

const AdminAppointments = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [activeAppt, setActiveAppt] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState('');

  useEffect(() => {
    if (localStorage.getItem('adminLoggedIn') !== 'true') {
      navigate('/admin/login');
      return;
    }
    refresh();
  }, [navigate]);

  const refresh = async () => {
    try {
      setLoading(true);
      const data = await getAppointments();
      setAppointments(data);
    } catch (err) {
      console.error('Error loading appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let list = [...appointments];

    if (statusFilter !== 'all') {
      list = list.filter((a) => (a.status || 'pending') === statusFilter);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((a) =>
        [a.name, a.email, a.phone, a.treatment]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(q))
      );
    }

    list.sort((a, b) => {
      let av, bv;
      if (sortBy === 'name') {
        av = (a.name || '').toLowerCase();
        bv = (b.name || '').toLowerCase();
      } else if (sortBy === 'status') {
        av = a.status || 'pending';
        bv = b.status || 'pending';
      } else {
        av = new Date(a.date || 0).getTime();
        bv = new Date(b.date || 0).getTime();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [appointments, search, statusFilter, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const visible = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sortBy, sortDir]);

  const counts = useMemo(() => {
    const acc = { all: appointments.length, pending: 0, confirmed: 0, completed: 0, cancelled: 0 };
    appointments.forEach((a) => {
      const s = a.status || 'pending';
      if (acc[s] !== undefined) acc[s] += 1;
    });
    return acc;
  }, [appointments]);

  const openEdit = (appt) => {
    setActiveAppt(appt);
    setStatusUpdate(appt.status || 'pending');
    setEditModalOpen(true);
  };

  const openView = (appt) => {
    setActiveAppt(appt);
    setViewModalOpen(true);
  };

  const saveStatus = async () => {
    try {
      await updateAppointmentStatus(activeAppt.id, statusUpdate);
      setAppointments((prev) =>
        prev.map((a) => (a.id === activeAppt.id ? { ...a, status: statusUpdate } : a))
      );
      setEditModalOpen(false);
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this appointment? This cannot be undone.')) return;
    try {
      await deleteAppointment(id);
      setAppointments((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Error deleting appointment:', err);
    }
  };

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const sortIcon = (col) => {
    if (sortBy !== col) return null;
    return (
      <FontAwesomeIcon
        icon={sortDir === 'asc' ? faArrowUpShortWide : faArrowDownShortWide}
        className="ms-1"
      />
    );
  };

  const filterChips = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'pending', label: 'Pending', count: counts.pending },
    { key: 'confirmed', label: 'Confirmed', count: counts.confirmed },
    { key: 'completed', label: 'Completed', count: counts.completed },
    { key: 'cancelled', label: 'Cancelled', count: counts.cancelled }
  ];

  return (
    <div className="admin-appointments">
      <header className="admin-page-head">
        <div>
          <span className="admin-page-eyebrow">Bookings</span>
          <h1 className="admin-page-title">Appointments</h1>
          <p className="admin-page-subtitle">
            Manage every booking submitted through the website. Search, filter, update status, or remove entries.
          </p>
        </div>
        <div className="admin-page-actions">
          <Button onClick={refresh} className="admin-btn admin-btn-ghost">
            <FontAwesomeIcon icon={faRotateRight} className="me-2" /> Refresh
          </Button>
        </div>
      </header>

      <section className="admin-panel">
        <div className="admin-toolbar">
          <div className="admin-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="admin-search-icon" />
            <input
              type="search"
              className="admin-search-input"
              placeholder="Search by name, email, phone, or treatment…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="admin-filter-chips" role="tablist" aria-label="Filter by status">
            <FontAwesomeIcon icon={faFilter} className="admin-filter-icon" />
            {filterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                role="tab"
                aria-selected={statusFilter === chip.key}
                className={`admin-filter-chip ${statusFilter === chip.key ? 'is-active' : ''}`}
                onClick={() => setStatusFilter(chip.key)}
              >
                {chip.label}
                <span className="admin-filter-chip-count">{chip.count}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="admin-loading-state">
            <Spinner animation="border" role="status" />
            <span>Loading appointments…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="admin-empty-state">
            <div className="admin-empty-icon">
              <FontAwesomeIcon icon={faCalendarCheck} />
            </div>
            <h4>No appointments match your filter</h4>
            <p>Try adjusting your search or status filter to see more results.</p>
          </div>
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th
                      className="admin-table-th-sortable"
                      onClick={() => toggleSort('name')}
                    >
                      Patient {sortIcon('name')}
                    </th>
                    <th
                      className="admin-table-th-sortable"
                      onClick={() => toggleSort('date')}
                    >
                      Date &amp; Time {sortIcon('date')}
                    </th>
                    <th>Treatment</th>
                    <th
                      className="admin-table-th-sortable"
                      onClick={() => toggleSort('status')}
                    >
                      Status {sortIcon('status')}
                    </th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <div className="admin-patient-cell">
                          <div className="admin-patient-avatar">
                            {(a.name || 'NA').charAt(0).toUpperCase()}
                          </div>
                          <div className="admin-patient-info">
                            <span className="admin-patient-name">{a.name || 'Unknown'}</span>
                            <span className="admin-patient-contact">
                              <FontAwesomeIcon icon={faPhone} className="me-1" />
                              {a.phone || '—'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="admin-date-cell">
                          <span className="admin-date-day">{formatDate(a.date)}</span>
                          <span className="admin-date-time">{a.time || '—'}</span>
                        </div>
                      </td>
                      <td>
                        <span className="admin-treatment-cell">{a.treatment || 'Consultation'}</span>
                      </td>
                      <td>{renderStatus(a.status || 'pending')}</td>
                      <td className="text-end">
                        <div className="admin-row-actions">
                          <button
                            type="button"
                            className="admin-icon-btn"
                            title="View details"
                            onClick={() => openView(a)}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          <button
                            type="button"
                            className="admin-icon-btn"
                            title="Update status"
                            onClick={() => openEdit(a)}
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </button>
                          <button
                            type="button"
                            className="admin-icon-btn admin-icon-btn--danger"
                            title="Delete"
                            onClick={() => handleDelete(a.id)}
                          >
                            <FontAwesomeIcon icon={faTrashCan} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="admin-pagination-bar">
              <span className="admin-pagination-info">
                Showing <strong>{pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)}</strong> of <strong>{filtered.length}</strong>
              </span>
              {totalPages > 1 && (
                <Pagination className="admin-pagination">
                  <Pagination.Prev
                    disabled={safePage === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  />
                  {Array.from({ length: totalPages }).map((_, idx) => {
                    const num = idx + 1;
                    if (
                      num === 1 ||
                      num === totalPages ||
                      (num >= safePage - 1 && num <= safePage + 1)
                    ) {
                      return (
                        <Pagination.Item
                          key={num}
                          active={num === safePage}
                          onClick={() => setPage(num)}
                        >
                          {num}
                        </Pagination.Item>
                      );
                    }
                    if (num === safePage - 2 || num === safePage + 2) {
                      return <Pagination.Ellipsis key={num} disabled />;
                    }
                    return null;
                  })}
                  <Pagination.Next
                    disabled={safePage === totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  />
                </Pagination>
              )}
            </div>
          </>
        )}
      </section>

      {/* Edit modal */}
      <Modal show={editModalOpen} onHide={() => setEditModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Appointment Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeAppt && (
            <>
              <div className="admin-modal-summary">
                <div>
                  <span className="admin-modal-label">Patient</span>
                  <span className="admin-modal-value">{activeAppt.name}</span>
                </div>
                <div>
                  <span className="admin-modal-label">Date</span>
                  <span className="admin-modal-value">{formatDate(activeAppt.date)} · {activeAppt.time}</span>
                </div>
                <div>
                  <span className="admin-modal-label">Treatment</span>
                  <span className="admin-modal-value">{activeAppt.treatment || '—'}</span>
                </div>
                <div>
                  <span className="admin-modal-label">Contact</span>
                  <span className="admin-modal-value">
                    <FontAwesomeIcon icon={faEnvelope} className="me-2" />{activeAppt.email || '—'}
                  </span>
                </div>
              </div>
              <Form.Group className="mt-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={statusUpdate}
                  onChange={(e) => setStatusUpdate(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setEditModalOpen(false)}>
            <FontAwesomeIcon icon={faCircleXmark} className="me-2" />Cancel
          </Button>
          <Button className="admin-btn admin-btn-primary" onClick={saveStatus}>
            <FontAwesomeIcon icon={faCircleCheck} className="me-2" />Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View modal */}
      <Modal show={viewModalOpen} onHide={() => setViewModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Appointment Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeAppt && (
            <div className="admin-detail-grid">
              <div className="admin-detail-row">
                <span className="admin-modal-label">Patient Name</span>
                <span className="admin-modal-value">{activeAppt.name || '—'}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-modal-label">Email</span>
                <span className="admin-modal-value">{activeAppt.email || '—'}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-modal-label">Phone</span>
                <span className="admin-modal-value">{activeAppt.phone || '—'}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-modal-label">Date</span>
                <span className="admin-modal-value">{formatDate(activeAppt.date)}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-modal-label">Time</span>
                <span className="admin-modal-value">{activeAppt.time || '—'}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-modal-label">Treatment</span>
                <span className="admin-modal-value">{activeAppt.treatment || '—'}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-modal-label">Status</span>
                <span className="admin-modal-value">{renderStatus(activeAppt.status || 'pending')}</span>
              </div>
              {activeAppt.message && (
                <div className="admin-detail-row admin-detail-row--full">
                  <span className="admin-modal-label">Message</span>
                  <span className="admin-modal-value">{activeAppt.message}</span>
                </div>
              )}
              <div className="admin-detail-row">
                <span className="admin-modal-label">Submitted</span>
                <span className="admin-modal-value">{formatDate(activeAppt.createdAt)}</span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setViewModalOpen(false)}>Close</Button>
          <Button
            className="admin-btn admin-btn-primary"
            onClick={() => {
              setViewModalOpen(false);
              openEdit(activeAppt);
            }}
          >
            <FontAwesomeIcon icon={faPenToSquare} className="me-2" />Update Status
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminAppointments;
